import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, missingFieldsFrom, type FormData, type PublicForm } from './api';
import { getAccessTokenOrNull, msalEnabled, signIn } from './auth/msal';
import { config, resolveAccessToken, resolveFormId } from './config';
import { FormioForm } from './FormioForm';

type Phase =
  | 'loading'
  | 'ready'
  | 'submitting'
  | 'submitted'
  | 'notfound'
  | 'error'
  | 'authrequired'
  | 'embedblocked';

interface DraftRef {
  submissionId: string;
  token: string;
}

function draftKey(formId: string): string {
  return `forms:draft:${formId}`;
}

function loadDraftRef(formId: string): DraftRef | null {
  try {
    const raw = localStorage.getItem(draftKey(formId));
    return raw ? (JSON.parse(raw) as DraftRef) : null;
  } catch {
    return null;
  }
}

/** A resume link carries the draft's id + token so an anonymous respondent can continue on any device. */
function resumeRefFromUrl(): DraftRef | null {
  const params = new URLSearchParams(window.location.search);
  const submissionId = params.get('rid');
  const token = params.get('rtok');
  return submissionId && token ? { submissionId, token } : null;
}

function postToParent(message: Record<string, unknown>): void {
  if (window.parent !== window) {
    window.parent.postMessage({ source: 'forms', ...message }, '*');
  }
}

function normalizeOrigin(value: string): string {
  const trimmed = value.trim().replace(/\/+$/, '').toLowerCase();
  try {
    // Accept either a bare origin or a full URL; compare on origin only.
    return new URL(trimmed).origin.toLowerCase();
  } catch {
    return trimmed;
  }
}

/** The immediate parent frame's origin, or null when not framed / undeterminable. */
function parentOrigin(): string | null {
  if (window.parent === window) {
    return null;
  }
  // ancestorOrigins is the reliable source in Chromium/WebKit; Firefox falls back to the referrer.
  const ancestors = window.location.ancestorOrigins;
  if (ancestors && ancestors.length > 0) {
    return ancestors[0].toLowerCase();
  }
  if (document.referrer) {
    try {
      return new URL(document.referrer).origin.toLowerCase();
    } catch {
      return null;
    }
  }
  return null;
}

/** Client-side embed guard: block rendering when framed by a non-allowlisted parent. */
function embedBlocked(allowed: string[] | null): boolean {
  if (!allowed || allowed.length === 0 || window.parent === window) {
    return false;
  }
  const origin = parentOrigin();
  if (!origin) {
    // Framed but the parent origin is opaque/unknown — fail closed.
    return true;
  }
  const allowSet = new Set(allowed.map(normalizeOrigin));
  return !allowSet.has(normalizeOrigin(origin));
}

export function App() {
  const formId = resolveFormId();
  const accessToken = resolveAccessToken();
  const [phase, setPhase] = useState<Phase>('loading');
  const [form, setForm] = useState<PublicForm | null>(null);
  const [initialData, setInitialData] = useState<FormData>({});
  const [readOnly, setReadOnly] = useState(false);
  const [missing, setMissing] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // null = not saved this session; 'account' = saved for a signed-in user; otherwise a resume URL.
  const [savedLink, setSavedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const dataRef = useRef<FormData>({});
  const draftRef = useRef<DraftRef | null>(null);
  const bearerRef = useRef<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load the form; sign-in-gated (Private) forms surface a 401 → authrequired.
  useEffect(() => {
    if (!formId) {
      setPhase('notfound');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const bearer = await getAccessTokenOrNull();
        bearerRef.current = bearer;
        const loaded = await api.getForm(formId, accessToken, bearer);
        if (cancelled) {
          return;
        }
        if (embedBlocked(loaded.embedAllowedOrigins)) {
          setPhase('embedblocked');
          return;
        }
        setForm(loaded);

        if (loaded.existing) {
          // Authenticated respondent with an existing submission: resume or view read-only.
          dataRef.current = loaded.existing.data;
          setInitialData(loaded.existing.data);
          setReadOnly(loaded.existing.status === 'Submitted');
        } else if (!bearer) {
          // Anonymous: resume from a "save & finish later" link if present, else a locally-stored draft.
          const stored = resumeRefFromUrl() ?? loadDraftRef(formId);
          if (stored) {
            try {
              const resumed = await api.resume(formId, stored.submissionId, stored.token, accessToken, null);
              draftRef.current = stored;
              dataRef.current = resumed.data;
              setInitialData(resumed.data);
              // Persist so subsequent autosaves reuse this draft (e.g. arrived via a resume link).
              localStorage.setItem(draftKey(formId), JSON.stringify(stored));
            } catch {
              localStorage.removeItem(draftKey(formId));
            }
          }
        }

        setPhase('ready');
        postToParent({ type: 'forms:loaded', formId });
      } catch (e) {
        if (cancelled) {
          return;
        }
        if (e instanceof ApiError && e.status === 401) {
          setPhase('authrequired');
        } else if (e instanceof ApiError && e.status === 404) {
          setPhase('notfound');
        } else {
          setError(e instanceof Error ? e.message : 'Failed to load form');
          setPhase('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formId, accessToken]);

  // Report height to a parent frame for iframe auto-resize. The document root's box
  // stays at viewport height (content overflows via scrollHeight), so a ResizeObserver
  // never fires — watch DOM mutations instead and report the full scroll height.
  useEffect(() => {
    let last = -1;
    let raf = 0;
    const post = () => {
      raf = 0;
      const height = Math.ceil(document.documentElement.scrollHeight);
      if (height !== last) {
        last = height;
        postToParent({ type: 'forms:resize', height, formId });
      }
    };
    const schedule = () => {
      if (!raf) {
        raf = requestAnimationFrame(post);
      }
    };
    // Post the first height synchronously — requestAnimationFrame is paused while the
    // frame is hidden (background tab or below the fold), so relying on it alone would
    // leave the parent without an initial size until the frame becomes visible.
    post();
    const mutations = new MutationObserver(schedule);
    mutations.observe(document.body, { subtree: true, childList: true, attributes: true, characterData: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
    // Flush the latest height when the frame becomes visible (rAF resumes then).
    document.addEventListener('visibilitychange', post);
    return () => {
      mutations.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('load', schedule);
      document.removeEventListener('visibilitychange', post);
      if (raf) {
        cancelAnimationFrame(raf);
      }
    };
  }, [formId]);

  const scheduleAutosave = useCallback(() => {
    if (!formId || readOnly) {
      return;
    }
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }
    saveTimer.current = setTimeout(async () => {
      try {
        const saved = await api.saveDraft(formId, dataRef.current, accessToken, bearerRef.current, draftRef.current?.token);
        if (!bearerRef.current) {
          // Anonymous drafts are resumed via a token stored locally.
          draftRef.current = { submissionId: saved.submissionId, token: saved.draftToken };
          localStorage.setItem(draftKey(formId), JSON.stringify(draftRef.current));
        }
      } catch {
        // Autosave is best-effort; ignore transient failures.
      }
    }, 1000);
  }, [formId, accessToken, readOnly]);

  const onChange = useCallback(
    (data: FormData) => {
      dataRef.current = data;
      scheduleAutosave();
    },
    [scheduleAutosave],
  );

  async function submit() {
    if (!formId) {
      return;
    }
    setPhase('submitting');
    setMissing([]);
    setError(null);
    try {
      const bearer = await getAccessTokenOrNull();
      await api.submit(formId, dataRef.current, accessToken, bearer, draftRef.current?.token);
      localStorage.removeItem(draftKey(formId));
      setPhase('submitted');
      postToParent({ type: 'forms:submitted', formId });
    } catch (e) {
      const missingFields = missingFieldsFrom(e);
      if (missingFields.length > 0) {
        setMissing(missingFields);
        setPhase('ready');
      } else {
        setError(e instanceof Error ? e.message : 'Submission failed');
        setPhase('error');
      }
    }
  }

  async function saveAndFinishLater() {
    if (!formId) {
      return;
    }
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }
    setSaving(true);
    setError(null);
    setCopied(false);
    try {
      const saved = await api.saveDraft(formId, dataRef.current, accessToken, bearerRef.current, draftRef.current?.token);
      if (bearerRef.current) {
        setSavedLink('account');
      } else {
        draftRef.current = { submissionId: saved.submissionId, token: saved.draftToken };
        localStorage.setItem(draftKey(formId), JSON.stringify(draftRef.current));
        const base = `${window.location.origin}${window.location.pathname}`;
        setSavedLink(`${base}?rid=${saved.submissionId}&rtok=${encodeURIComponent(saved.draftToken)}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your progress');
    } finally {
      setSaving(false);
    }
  }

  async function copyResumeLink() {
    if (!savedLink || savedLink === 'account') {
      return;
    }
    try {
      await navigator.clipboard.writeText(savedLink);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  if (phase === 'loading') {
    return <div className="wrap">Loading…</div>;
  }
  if (phase === 'notfound') {
    return <div className="wrap">This form is not available.</div>;
  }
  if (phase === 'embedblocked') {
    return <div className="wrap">This form cannot be embedded on this site.</div>;
  }
  if (phase === 'authrequired') {
    return (
      <div className="wrap">
        <h2>Sign in required</h2>
        <p>This form is private. Sign in with your organisation account to continue.</p>
        {msalEnabled ? (
          <button className="submit" onClick={() => void signIn()}>Sign in</button>
        ) : (
          <p className="error">Sign-in is not configured for this site.</p>
        )}
      </div>
    );
  }
  if (phase === 'submitted') {
    return (
      <div className="wrap">
        <h2>Thank you</h2>
        <p>Your response has been submitted.</p>
      </div>
    );
  }

  return (
    <div className="wrap">
      {form && <h1>{form.title}</h1>}
      {form?.consentText && <p className="consent">{form.consentText}</p>}
      {readOnly && <p className="note">You have already submitted this form. It is shown here read-only.</p>}

      {missing.length > 0 && (
        <p className="error">Please complete the required fields: {missing.join(', ')}.</p>
      )}
      {error && <p className="error">{error}</p>}

      {form && (
        <FormioForm
          key={form.id}
          schema={form.schema}
          initialData={initialData}
          onChange={onChange}
          readOnly={readOnly}
          fileUploadUrl={`${config.apiBaseUrl}/api/public/forms/${form.id}/files${accessToken ? `?t=${encodeURIComponent(accessToken)}` : ''}`}
        />
      )}

      {!readOnly && (
        <div className="submit-row">
          <button className="submit" disabled={phase === 'submitting'} onClick={submit}>
            {phase === 'submitting' ? 'Submitting…' : 'Submit'}
          </button>
          {form?.allowSaveAndResume && (
            <button className="secondary" disabled={saving || phase === 'submitting'} onClick={saveAndFinishLater}>
              {saving ? 'Saving…' : 'Save & finish later'}
            </button>
          )}
        </div>
      )}

      {savedLink === 'account' && (
        <p className="note">Saved. Sign in again any time to continue where you left off.</p>
      )}
      {savedLink && savedLink !== 'account' && (
        <div className="note">
          <p>Your progress is saved. Use this link on any device to continue later:</p>
          <div className="resume-row">
            <code className="resume-link">{savedLink}</code>
            <button className="secondary" onClick={copyResumeLink}>{copied ? 'Copied' : 'Copy'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
